import { faArrowDown, faArrowUp, faBars, faMagnifyingGlass, faSliders } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import 'intersection-observer'
import { useEffect, useRef, useState } from 'react'
import api, { Token, Trait } from '../api'
import assets from '../assets'
import { CollectionViewCard } from './CollectionViewCard'

type CollectionViewProps = {
    tokens: Token[]
    className?: string
}

type SortStateType = {
    operative_id: number | null
    rank: number | null
    price: number | null
}

export const CollectionView = ({ tokens, className = '' }: CollectionViewProps) => {
    const pageSize = 20

    const [traits, setTraits] = useState<Record<string, Trait[]>>({})
    const [traitsLoaded, setTraitsLoaded] = useState(false)
    const [tokensFiltered, setTokensFiltered] = useState(tokens)
    const [tokensView, setTokensView] = useState(tokensFiltered.slice(0, pageSize))
    const [lastElement, setLastElement] = useState(null)
    const [page, setPage] = useState(0)
    const [sortState, setSortState] = useState<SortStateType>({
        operative_id: null,
        rank: null,
        price: -1,
    })

    const [operativeTypes, setOperativeTypes] = useState<string[]>([])
    const [filteringComplete, setFilteringComplete] = useState(true)
    const [sortingComplete, setSortingComplete] = useState(true)

    useEffect(() => {
        api.collection
            .traits()
            .then((traits) => setTraits(traits))
            .finally(() => setTraitsLoaded(true))
    }, [])

    useEffect(() => {
        setTokensFiltered(tokens)
        setOperativeTypes(Array.from(new Set(tokens.map((t) => t.operative_type))))
    }, [tokens])

    useEffect(() => {
        if (traitsLoaded)
            setTraits(
                Object.fromEntries(
                    Object.entries(traits).filter(([group, traits]) => (operativeTypes.includes(group) ? true : false))
                )
            )
    }, [operativeTypes, traitsLoaded])

    // State for filters toggle

    const [filterToggleState, setFilterToggleState] = useState<Array<[string, string]>>([])

    const toggleFilter = (key, name) => {
        if (filterToggleState.findIndex(([k, t]) => k == key && t == name) !== -1) {
            setFilterToggleState((prev) => prev.filter(([k, t]) => !(k == key && t == name)))
        } else {
            setFilterToggleState((prev) => [...prev, [key, name]])
        }
    }

    // Filter icons
    const filterIcons = {
        RARITY: assets.collection_info.rarity_icon,
        'HEADGEAR MATERIAL': assets.collection_info.hat_icon,
        'BODY PATTERN': assets.collection_info.bodypattern_icon,
        'BODY PATTERN COLOR': assets.collection_info.colordrop_icon,
        'BODY TATTOO': assets.collection_info.tattoo_icon,
        'TATTOO COLOR': assets.collection_info.colordrop_icon,
        'SHORTS DESIGN': assets.collection_info.shorts_icon,
        'SHORTS COLOR': assets.collection_info.colordrop_icon,
        'SHORTS SECOND COLOR': assets.collection_info.colordrop_add_icon,
        'SHORTS LOGO': assets.collection_info.tattoo_icon,
        'SHORTS LOGO COLOR': assets.collection_info.colordrop_icon,
        'SKIN COLOR': assets.collection_info.colordrop_icon,

        'DOMINANT COLOR': assets.collection_info.colordrop_icon,
        'SECONDARY COLOR': assets.collection_info.colordrop_add_icon,
        'ARMOUR MATERIAL': assets.collection_info.bodypattern_icon,
    }

    const filterBorders = {
        ape: '#C91FFF',
        droid: '#03CEFF',
    }

    // Filtering
    const [traitsFilter, setTraitsFilter] = useState<Array<[string, string, string]>>([])
    const [rarityFilter, setRarityFilter] = useState<string[]>([])
    const [operativeTypeFilter, setOperativeTypeFilter] = useState<string[]>([])
    const [idFilter, setIdFilter] = useState<number | undefined>()
    const [rankFilter, setRankFilter] = useState<number | undefined>()
    const filterContainerRef = useRef<HTMLDivElement>(null)
    const [filtersVisible, setFiltersVisible] = useState(false)
    const [filterChanged, setFilterChanged] = useState(1)

    useEffect(() => {
        const media = window.matchMedia('(min-width: 768px)')
        const listener = () => setFiltersVisible(media.matches)
        listener()
        window.addEventListener('resize', listener)

        return () => window.removeEventListener('resize', listener)
    }, [])

    const applyTraitsFilter = (operativeType, type, value) => {
        if (traitsFilter.find(([o, t, v]) => o == operativeType && t == type && v == value)) {
            setTraitsFilter((prev) => prev.filter(([o, t, v]) => !(o == operativeType && t == type && v == value)))
        } else {
            setTraitsFilter((prev) => [...prev, [operativeType, type, value]])
        }
    }

    const applyRarityFilter = (rarity) => {
        if (rarityFilter.includes(rarity)) setRarityFilter((prev) => prev.filter((p) => p !== rarity))
        else setRarityFilter((prev) => [...prev, rarity])
    }

    const applyOperativeTypeFilter = (rarity) => {
        if (operativeTypeFilter.includes(rarity)) setOperativeTypeFilter((prev) => prev.filter((p) => p !== rarity))
        else setOperativeTypeFilter((prev) => [...prev, rarity])
    }

    const applySearchFilter = (idOrRank: string) => {
        let id
        let rank
        if (idOrRank.startsWith('#')) id = idOrRank.slice(1)
        else rank = idOrRank

        setIdFilter(parseInt(id))
        setRankFilter(parseInt(rank))
    }

    const filterTraits = (tokens, attributeFilter) => {
        const types = Array.from(new Set(attributeFilter.map(([operativeType, traitType, value]) => traitType)))
        return tokens.filter((t) => {
            return types.every((type) => {
                return attributeFilter.some(([operativeType, traitType, value]) => {
                    return (
                        operativeType == t.operative_type &&
                        traitType == type &&
                        t.traits.some((ta) => ta.type == traitType && ta.value == value)
                    )
                })
            })
        })
    }

    const filterRarity = (tokens, rarityFilter) => {
        return tokens.filter((t) => {
            if ([0, 2].includes(rarityFilter.length)) return true
            if (t.is_legendary && rarityFilter.includes('LEGENDARY')) return true
            if (!t.is_legendary && rarityFilter.includes('COMMON')) return true
            return false
        })
    }

    const filterOperativeType = (tokens, operativeTypeFilter) => {
        return tokens.filter((t) => {
            if ([0, 2].includes(operativeTypeFilter.length)) return true
            if (t.operative_type.toUpperCase() === 'APE' && operativeTypeFilter.includes('APE')) return true
            if (t.operative_type.toUpperCase() === 'DROID' && operativeTypeFilter.includes('DROID')) return true
            return false
        })
    }

    const filterId = (tokens, id) => {
        return tokens.filter((t) => t.operative_id.toString() == id.toString())
    }

    const filterRank = (tokens, rank) => {
        return tokens.filter((t) => t.rank.toString() == rank.toString())
    }

    const clearFilters = () => {
        setTraitsFilter([])
        setOperativeTypeFilter([])
        setRarityFilter([])
        filterContainerRef.current
            ?.querySelectorAll('input[type=checkbox]')
            .forEach((i) => ((i as HTMLInputElement).checked = false))
    }

    useEffect(() => {
        setFilteringComplete(false)
        let filtered = tokens

        if (traitsFilter.length > 0) {
            filtered = filterTraits(filtered, traitsFilter)
        }

        if (rarityFilter.length > 0) {
            filtered = filterRarity(filtered, rarityFilter)
        }

        if (operativeTypeFilter.length > 0) {
            filtered = filterOperativeType(filtered, operativeTypeFilter)
        }

        if (idFilter) filtered = filterId(filtered, idFilter)
        if (rankFilter) filtered = filterRank(filtered, rankFilter)

        setTokensFiltered(filtered)
        setFilterChanged((prev) => prev * -1)
        setFilteringComplete(true)
    }, [traitsFilter, rarityFilter, operativeTypeFilter, idFilter, rankFilter])

    // Count values per filtering groups

    const traitsCount = (operative_type, type, value) => {
        let filtered = filterTraits(
            tokens,
            traitsFilter.filter(([operativeType, traitType, v]) => traitType != type)
        ).filter(
            (t) => t.operative_type == operative_type && t.traits.some((ta) => ta.type == type && ta.value == value)
        )

        filtered = filterRarity(filtered, rarityFilter)
        filtered = filterOperativeType(filtered, operativeTypeFilter)
        if (idFilter) filtered = filterId(filtered, idFilter)
        if (rankFilter) filtered = filterRank(filtered, rankFilter)

        return filtered.length
    }

    const rarityCount = (rarity) => {
        let filtered = filterTraits(tokens, traitsFilter)
        filtered = filterOperativeType(filtered, operativeTypeFilter)
        if (idFilter) filtered = filterId(filtered, idFilter)
        if (rankFilter) filtered = filterRank(filtered, rankFilter)
        return filtered.filter((t) =>
            (t.is_legendary && rarity == 'LEGENDARY') || (!t.is_legendary && rarity == 'COMMON') ? true : false
        ).length
    }

    const operativesCount = (type) => {
        let filtered = filterTraits(tokens, traitsFilter)
        filtered = filterRarity(filtered, rarityFilter)
        if (idFilter) filtered = filterId(filtered, idFilter)
        if (rankFilter) filtered = filterRank(filtered, rankFilter)
        return filtered.filter((t) => t.operative_type.toUpperCase() === type).length
    }

    // Paging

    useEffect(() => {
        setPage(0)
        setTokensView(tokensFiltered.slice(0, (page + 1) * pageSize))
    }, [tokensFiltered])

    useEffect(() => {
        setTokensView(tokensFiltered.slice(0, (page + 1) * pageSize))
    }, [page])

    // When last rendered Card appear in view - inc rendered size by pageSize
    useEffect(() => {
        if (lastElement) {
            const observer = new IntersectionObserver((entries) => {
                const first = entries[0]
                if (first.isIntersecting) {
                    setPage((prev) => prev + 1)
                }
            })

            observer.observe(lastElement)

            return () => {
                observer.unobserve(lastElement)
            }
        }
    }, [lastElement])

    // Sorting

    const sort = (field) => {
        const current = sortState[field]

        const newSortState = {
            operative_id: null,
            rank: null,
            price: null,
        }

        newSortState[field] = current == 1 || current == null ? -1 : 1
        setSortState(newSortState)
    }

    useEffect(() => {
        setSortingComplete(false)

        const [field, order] = Object.entries(sortState).filter(([f, o]) => o !== null)[0]

        if (field) {
            setTokensFiltered([
                ...tokensFiltered.sort((a, b) => {
                    // equal items sort equally
                    if (a[field] === b[field]) {
                        return 0
                    }
                    // nulls sort after anything else
                    else if (a[field] === null) {
                        return 1
                    } else if (b[field] === null) {
                        return -1
                    }
                    // otherwise, if we're ascending, lowest sorts first
                    else if (order == -1) {
                        return a[field] > b[field] ? 1 : -1
                    }
                    // if descending, highest sorts first
                    else {
                        return a[field] < b[field] ? 1 : -1
                    }
                }),
            ])
        }
        setSortingComplete(true)
    }, [sortState, filterChanged])

    return (
        <div className={'flex flex-col w-full max-w-[1920px] mx-auto ' + className}>
            <section className='flex flex-col md:flex-row items-center justify-end w-full gap-4 md:py-8 text-xs md:text-base'>
                <div className='group flex flex-row gap-2 items-center px-4 py-2 rounded-3xl border'>
                    <FontAwesomeIcon icon={faMagnifyingGlass} flip='horizontal' className='text-sm'></FontAwesomeIcon>
                    <input
                        maxLength={6}
                        type='text'
                        placeholder='#ID OR RANK'
                        className='text-sm font-bold bg-transparent focus:outline-none w-full'
                        onChange={(e) => applySearchFilter(e.target.value)}
                    />
                </div>
                <div className='flex flex-row gap-x-8 px-4 items-center place-content-center font-bold'>
                    <button className='md:hidden py-3 px-1' onClick={() => setFiltersVisible((prev) => !prev)}>
                        <FontAwesomeIcon icon={faSliders} className='text-base' />
                    </button>
                    <div>SORT BY:</div>
                    {[
                        ['operative_id', 'id'],
                        ['rank', 'rank'],
                        ['price', 'price'],
                    ].map(([field, name], i) => (
                        <button key={field} onClick={() => sort(field)} className='flex flex-row items-center gap-x-1'>
                            <span
                                className={`uppercase font-bold ${sortState[field] == 1 && 'text-[#D40000]'} ${
                                    sortState[field] == -1 && 'text-[#00FF90]'
                                }`}
                            >
                                {name}
                            </span>
                            {sortState[field] ? (
                                <FontAwesomeIcon icon={sortState[field] == -1 ? faArrowDown : faArrowUp} />
                            ) : (
                                ' '
                            )}
                        </button>
                    ))}
                </div>
            </section>

            <div
                className='hidden md:block h-0.5 w-3/4 self-end'
                style={{ background: 'linear-gradient(270deg, #D9D9D9 0%, rgba(217, 217, 217, 0) 100%)' }}
            >
                &nbsp;
            </div>

            <div className={`flex flex-col items-center md:items-start md:flex-row pt-2 md:pl-4 `}>
                {!filtersVisible ? (
                    <button className='py-3 px-1 hidden md:block' onClick={() => setFiltersVisible((prev) => !prev)}>
                        <FontAwesomeIcon icon={faSliders} className='text-base' />
                    </button>
                ) : (
                    <div ref={filterContainerRef} className={`${filtersVisible ? 'block' : 'hidden'}`}>
                        <div className='flex flex-row justify-between items-center text-xs py-4 px-1'>
                            <button onClick={() => setFiltersVisible((prev) => !prev)}>
                                <FontAwesomeIcon icon={faSliders} className='text-base hidden md:block' />
                            </button>
                            <button onClick={clearFilters}>CLEAR</button>
                        </div>

                        {operativeTypes.length > 1 && (
                            <div className='relative select-none uppercase text-xs flex flex-col m-1'>
                                <div
                                    className='absolute top-0 left-0 h-full w-full'
                                    style={{ background: 'linear-gradient(99.71deg, #C91FFF -4.52%, #03CEFF 106.52%)' }}
                                >
                                    &nbsp;
                                </div>
                                <div className='z-10 bg-[#151218] p-3 m-[1px]'>
                                    <div
                                        onClick={() => toggleFilter('', 'OPERATIVE_TYPE')}
                                        className='flex flex-row gap-x-4 items-center cursor-pointer'
                                    >
                                        <img src={filterIcons.RARITY} className='h-3 w-3' />
                                        <div className='flex-1 font-bold'>OPERATIVE TYPE</div>
                                        <div
                                            className={`border-t border-r w-2 h-2  ${
                                                filterToggleState.find(
                                                    ([k, t]) => t.toLocaleUpperCase() == 'OPERATIVE_TYPE'
                                                )
                                                    ? 'rotate-[135deg]'
                                                    : 'rotate-45'
                                            } transition-transform`}
                                        >
                                            &nbsp;
                                        </div>
                                    </div>
                                    <div
                                        className={`flex-col gap-y-2 mt-4  ${
                                            filterToggleState.find(
                                                ([k, t]) => t.toLocaleUpperCase() == 'OPERATIVE_TYPE'
                                            )
                                                ? 'flex'
                                                : 'hidden'
                                        }`}
                                    >
                                        {['APE', 'DROID'].map((type) => (
                                            <label
                                                key={type}
                                                className='flex flex-row flex-nowrap gap-x-4 cursor-pointer'
                                            >
                                                <input
                                                    type='checkbox'
                                                    className='self-center appearance-none h-3 w-3 border checked:bg-[#C91FFF] cursor-pointer'
                                                    onChange={() => applyOperativeTypeFilter(type)}
                                                />
                                                {type} [{operativesCount(type)}]
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='relative select-none uppercase text-xs flex flex-col m-1'>
                            <div
                                className='absolute top-0 left-0 h-full w-full'
                                style={{ background: 'linear-gradient(99.71deg, #C91FFF -4.52%, #03CEFF 106.52%)' }}
                            >
                                &nbsp;
                            </div>
                            <div className='z-10 bg-[#151218] p-3 m-[1px]'>
                                <div
                                    onClick={() => toggleFilter('', 'RARITY')}
                                    className='flex flex-row gap-x-4 items-center cursor-pointer'
                                >
                                    <img src={filterIcons.RARITY} className='h-3 w-3' />
                                    <div className='flex-1 font-bold'>RARITY</div>
                                    <div
                                        className={`border-t border-r w-2 h-2  ${
                                            filterToggleState.find(([k, t]) => t.toLocaleUpperCase() == 'RARITY')
                                                ? 'rotate-[135deg]'
                                                : 'rotate-45'
                                        } transition-transform`}
                                    >
                                        &nbsp;
                                    </div>
                                </div>
                                <div
                                    className={`flex-col gap-y-2 mt-4  ${
                                        filterToggleState.find(([k, t]) => t.toLocaleUpperCase() == 'RARITY')
                                            ? 'flex'
                                            : 'hidden'
                                    }`}
                                >
                                    {['COMMON', 'LEGENDARY'].map((rarity) => (
                                        <label
                                            key={rarity}
                                            className='flex flex-row flex-nowrap gap-x-4 cursor-pointer'
                                        >
                                            <input
                                                type='checkbox'
                                                className='self-center appearance-none h-3 w-3 border checked:bg-[#C91FFF] cursor-pointer'
                                                onChange={() => applyRarityFilter(rarity)}
                                            />
                                            {rarity} [{rarityCount(rarity)}]
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {Object.entries(traits).map(
                            ([key, group]) =>
                                group.length > 0 && (
                                    <div key={key}>
                                        <div className='uppercase text-left pl-1 mt-2'>{key}s</div>
                                        {Array.from(new Set(group.map((a) => a.type))).map((name) => {
                                            return (
                                                <div
                                                    key={name}
                                                    className='select-none uppercase text-xs border flex flex-col p-3 m-1'
                                                    style={{ borderColor: filterBorders[key] }}
                                                >
                                                    <div
                                                        onClick={() => toggleFilter(key, name)}
                                                        className='flex flex-row gap-x-4 items-center cursor-pointer'
                                                    >
                                                        {filterIcons[name.toUpperCase()] ? (
                                                            <img
                                                                src={filterIcons[name.toUpperCase()]}
                                                                className='h-3 w-3'
                                                            />
                                                        ) : (
                                                            <FontAwesomeIcon
                                                                icon={faBars}
                                                                className='h-3 w-3'
                                                            ></FontAwesomeIcon>
                                                        )}
                                                        <div className='flex-1 font-bold'>{name}</div>
                                                        <div
                                                            className={`border-t border-r w-2 h-2  ${
                                                                filterToggleState.find(
                                                                    ([k, t]) => k == key && t == name
                                                                )
                                                                    ? 'rotate-[135deg]'
                                                                    : 'rotate-45'
                                                            } transition-transform`}
                                                        >
                                                            &nbsp;
                                                        </div>
                                                    </div>
                                                    <div
                                                        className={`flex-col gap-y-2 mt-4  ${
                                                            filterToggleState.find(([k, t]) => k == key && t == name)
                                                                ? 'flex'
                                                                : 'hidden'
                                                        }`}
                                                    >
                                                        {group
                                                            .filter((a) => a.type == name)
                                                            .map(({ value }) => (
                                                                <label
                                                                    key={value}
                                                                    className='flex flex-row flex-nowrap gap-x-4 cursor-pointer'
                                                                >
                                                                    <input
                                                                        type='checkbox'
                                                                        className='self-center appearance-none h-3 w-3 border checked:bg-[#C91FFF] cursor-pointer'
                                                                        onChange={() =>
                                                                            applyTraitsFilter(key, name, value)
                                                                        }
                                                                    />
                                                                    {value}
                                                                    <span>[{traitsCount(key, name, value)}]</span>
                                                                </label>
                                                            ))}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )
                        )}
                    </div>
                )}

                <div className='flex-1'>
                    {tokensView.length == 0 && (
                        <div className='mt-8 w-full text-center text-gray-400'>No operatives found</div>
                    )}
                    <div className='grid grid-cols-1 gap-8 p-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 place-content-start'>
                        {filteringComplete &&
                            sortingComplete &&
                            tokensView.map((token, index) => (
                                <CollectionViewCard
                                    key={token.operative_id}
                                    token={token}
                                    ref={index == tokensView.length - 1 ? setLastElement : null}
                                />
                            ))}
                    </div>
                </div>
            </div>
        </div>
    )
}
